import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ListRow } from './ListRow';

interface VirtualizedObjectListProps {
  selectedObjects: any[];
  onQuantityChange: (id: string, quantity: number) => void;
  onView: (id: string) => void;
  onContractionChange: (id: string, value: string) => void;
  onToggleInclude: (id: string, include: boolean) => void;
  formatValue: (value: number) => string;
  role: string;
  showContraction?: boolean; // ✅ NOVO: Controla exibição do botão de contração
}

/**
 * Lista virtualizada de objetos
 * Usa @tanstack/react-virtual para performance otimizada com listas grandes
 */
export function VirtualizedObjectList({
  selectedObjects,
  onQuantityChange,
  onView,
  onContractionChange,
  onToggleInclude,
  formatValue,
  role,
  showContraction = true // ✅ Default: mostrar contração
}: VirtualizedObjectListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: selectedObjects.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 93, // Ajustado para incluir mb-2 (8px) + altura do card (~85px)
    overscan: 2,
  });

  // Empty state
  if (selectedObjects.length === 0) {
    return (
      <div className="flex-1 overflow-auto" ref={parentRef}>
        <div className="p-8 text-center text-gray-400">
          <p className="text-sm">Nenhum objeto encontrado</p>
          <p className="text-xs mt-1">Os objetos aparecerão aqui quando detectados</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto" ref={parentRef}>
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const index = virtualRow.index;
          const obj = selectedObjects[index];

          return (
            <ListRow
              key={obj.id}
              obj={obj}
              index={index + 1}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              onQuantityChange={onQuantityChange}
              onView={onView}
              formatValue={formatValue}
              onContractionChange={onContractionChange}
              role={role}
              onToggleInclude={onToggleInclude}
              showContraction={showContraction}
            />
          );
        })}
      </div>
    </div>
  );
}

